import { useEffect, useState } from 'react';
import { Field, Section } from './DeviceDetailsParts';
import DmrDiagnostics from './DmrDiagnostics';
import { CellularSignalField, WifiNetworkSection } from './DeviceNetworkSections';
import { IccidWithPortal, OnenceDeviceSim } from './OnenceSim';
import { useIdentity } from '../../services/identity';
import type { ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DeviceInfo,
  DeviceLog,
  MobileNetworkOperator,
  getDeviceInfo,
  refreshDeviceInfo,
  rebootDevice,
  shutdownDevice,
  setDisplayTimeout,
  getDeviceLogs,
  requestLogCollection,
  clearLogcat,
  downloadDeviceLogUrl,
  requestMobileNetworkScan,
  requestMobileNetworkSwitch,
  requestMobileNetworkAutomatic,
  deleteDeviceLog,
} from '../../services/admin';

const NOT_AVAILABLE = 'Not available';

const fmt = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === '' ? NOT_AVAILABLE : String(value);

// Formats a seconds-since-boot value (documentation/protocol.md) as a compact "1d 2h 3m" duration.
const formatUptime = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return NOT_AVAILABLE;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (days > 0 || hours > 0) parts.push(`${hours}h`);
  parts.push(`${minutes}m`);
  return parts.join(' ');
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Mirrors the local S380 UI's progress text (MobileNetworkSection.kt) so an admin sees the same
// wording whether they're standing next to the radio or watching remotely.
const switchStateLabel = (state: string | null): string => {
  switch (state) {
    case 'SWITCHING':
      return 'Switching...';
    case 'REGISTERING':
      return 'Registering on network...';
    case 'VERIFYING_CONNECTIVITY':
      return 'Checking mobile data and server connectivity...';
    case 'CONNECTED':
      return 'Connected.';
    case 'ROLLING_BACK':
      return 'Could not establish a usable connection. Returning to Automatic selection...';
    case 'FAILED':
      return 'Failed.';
    case 'IDLE':
      return 'Automatic selection confirmed.';
    default:
      return '';
  }
};

const LOG_TYPES: { type: string; label: string }[] = [
  { type: 'logcat', label: '📋 Logcat' },
  { type: 'dmesg', label: '⚙️ dmesg' },
  { type: 'crash', label: '💥 Crashes/ANRs' },
  { type: 'wakelocks', label: '🔋 Wake Locks' },
  { type: 'all_zip', label: '📦 Hent alle logs (ZIP)' },
];

const DISPLAY_TIMEOUT_OPTIONS: { ms: number; label: string }[] = [
  { ms: 15000, label: '15 seconds' },
  { ms: 30000, label: '30 seconds' },
  { ms: 60000, label: '1 minute' },
  { ms: 120000, label: '2 minutes' },
  { ms: 300000, label: '5 minutes' },
  { ms: 600000, label: '10 minutes' },
  { ms: 1800000, label: '30 minutes' },
];

export default function DeviceDetails() {
  const { radioId } = useParams<{ radioId: string }>();
  // 1NCE evidence and the full ICCID are platform data (the server masks it for everyone else).
  const { me } = useIdentity();
  const isPlatform = !!me?.platform_admin;
  const navigate = useNavigate();
  const [info, setInfo] = useState<DeviceInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [powerActionPending, setPowerActionPending] = useState<'reboot' | 'shutdown' | null>(null);
  const [powerActionMessage, setPowerActionMessage] = useState<string | null>(null);
  const [logs, setLogs] = useState<DeviceLog[]>([]);
  const [logActionPending, setLogActionPending] = useState<string | null>(null);
  const [logMessage, setLogMessage] = useState<string | null>(null);
  const [displayTimeoutPending, setDisplayTimeoutPending] = useState(false);
  // Optimistic value shown in the dropdown immediately on selection - the confirmed value from
  // the device only lands a couple of seconds later (see handleDisplayTimeoutChange).
  const [displayTimeoutOptimisticMs, setDisplayTimeoutOptimisticMs] = useState<number | null>(null);
  const [mobileNetworkPending, setMobileNetworkPending] = useState<'scan' | 'switch' | 'automatic' | null>(null);
  const [mobileNetworkMessage, setMobileNetworkMessage] = useState<string | null>(null);
  const [mobileNetworkConfirmTarget, setMobileNetworkConfirmTarget] = useState<MobileNetworkOperator | null>(null);
  // Re-evaluates report ages / stale markers without reloading.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  const load = () => {
    if (!radioId) return Promise.resolve();
    return getDeviceInfo(radioId).then(setInfo);
  };

  useEffect(() => {
    load()
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load device'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioId]);

  const loadLogs = () => {
    if (!radioId) return Promise.resolve();
    return getDeviceLogs(radioId).then(setLogs);
  };

  useEffect(() => {
    loadLogs().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radioId]);

  const handleRefresh = async () => {
    if (!radioId) return;
    setRefreshing(true);
    setRefreshMessage(null);
    try {
      await refreshDeviceInfo(radioId);
      setRefreshMessage('Requested - the device will report fresh data within a few seconds.');
      // The device's response arrives asynchronously (over its own WebSocket + re-registration),
      // not in this HTTP response - reload after a short wait to pick it up automatically.
      setTimeout(() => {
        load().catch(() => undefined);
      }, 4000);
    } catch (e) {
      setRefreshMessage(e instanceof Error ? e.message : 'Failed to request refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleReboot = async () => {
    if (!radioId) return;
    if (!window.confirm('Restart this device now? It will briefly go offline while it reboots.')) return;
    setPowerActionPending('reboot');
    setPowerActionMessage(null);
    try {
      await rebootDevice(radioId);
      setPowerActionMessage('Restart requested - the device will reboot shortly.');
    } catch (e) {
      setPowerActionMessage(e instanceof Error ? e.message : 'Failed to request restart');
    } finally {
      setPowerActionPending(null);
    }
  };

  const handleShutdown = async () => {
    if (!radioId) return;
    if (
      !window.confirm(
        'Shut down this device now? It will NOT come back online on its own - someone must physically press its power button to turn it back on.'
      )
    )
      return;
    setPowerActionPending('shutdown');
    setPowerActionMessage(null);
    try {
      await shutdownDevice(radioId);
      setPowerActionMessage('Shutdown requested - the device will power off shortly.');
    } catch (e) {
      setPowerActionMessage(e instanceof Error ? e.message : 'Failed to request shutdown');
    } finally {
      setPowerActionPending(null);
    }
  };

  const handleCollectLog = async (logType: string) => {
    if (!radioId) return;
    setLogActionPending(logType);
    setLogMessage(null);
    try {
      await requestLogCollection(radioId, logType);
      setLogMessage('Requested - the collected log will appear in the list below within a few seconds.');
      setTimeout(() => {
        loadLogs().catch(() => undefined);
      }, 4000);
    } catch (e) {
      setLogMessage(e instanceof Error ? e.message : 'Failed to request log collection');
    } finally {
      setLogActionPending(null);
    }
  };

  const handleClearLogcat = async () => {
    if (!radioId) return;
    setLogActionPending('clear');
    setLogMessage(null);
    try {
      await clearLogcat(radioId);
      setLogMessage('Requested - the device will clear its logcat buffer.');
    } catch (e) {
      setLogMessage(e instanceof Error ? e.message : 'Failed to request logcat clear');
    } finally {
      setLogActionPending(null);
    }
  };

  const handleDeleteLog = async (logId: string) => {
    if (!confirm('Delete this collected log? This cannot be undone.')) return;
    setLogActionPending(`delete-${logId}`);
    setLogMessage(null);
    try {
      await deleteDeviceLog(logId);
      await loadLogs();
    } catch (e) {
      setLogMessage(e instanceof Error ? e.message : 'Failed to delete log');
    } finally {
      setLogActionPending(null);
    }
  };

  // Shows the new value immediately (optimistic) and polls quickly (every 1s, up to ~8s) instead
  // of a single fixed 4s wait - the device confirms via its next heartbeat, which this triggers
  // immediately rather than waiting for the normal ~10s cycle (see PTTRadioViewModel.kt). Keeps
  // the dropdown disabled the whole time so a second change can't race the poll for the first.
  // 15s covers a normal round trip (WS push -> apply -> immediate heartbeat -> DB) with margin;
  // if it still hasn't confirmed by then, showing the real last-known value is more honest than
  // pretending the change is in effect.
  const pollDisplayTimeout = (requestedMs: number) => {
    const deadline = Date.now() + 15_000;
    const tick = async () => {
      if (!radioId) return;
      let fresh: DeviceInfo;
      try {
        fresh = await getDeviceInfo(radioId);
      } catch {
        if (Date.now() < deadline) setTimeout(tick, 1000);
        else {
          setDisplayTimeoutOptimisticMs(null);
          setDisplayTimeoutPending(false);
        }
        return;
      }
      setInfo(fresh);
      if (fresh.display_timeout_ms === requestedMs || Date.now() >= deadline) {
        setDisplayTimeoutOptimisticMs(null);
        setDisplayTimeoutPending(false);
        return;
      }
      setTimeout(tick, 1000);
    };
    setTimeout(tick, 1000);
  };

  const handleDisplayTimeoutChange = async (timeoutMs: number) => {
    if (!radioId) return;
    setDisplayTimeoutPending(true);
    setDisplayTimeoutOptimisticMs(timeoutMs);
    try {
      await setDisplayTimeout(radioId, timeoutMs);
      pollDisplayTimeout(timeoutMs);
    } catch (e) {
      setRefreshMessage(e instanceof Error ? e.message : 'Failed to set display timeout');
      setDisplayTimeoutOptimisticMs(null);
      setDisplayTimeoutPending(false);
    }
  };

  // Mobile Network remote control (Management Client Device Details > Network - see
  // documentation/decisions.md "S380 operator selection capability"). Commands are relayed to the
  // radio's own WebSocket connection and results arrive asynchronously; a scan can take up to
  // ~70 seconds, so this polls getDeviceInfo periodically (not busy-polling: every 5s, capped at
  // ~100s) instead of blocking on a single long HTTP request. The rollback/watchdog itself always
  // runs locally on the radio (see MobileNetworkManager.kt) - this page never implements or
  // depends on that logic, only relays the request and reads back the eventual result.
  const isMobileNetworkTerminal = (kind: 'scan' | 'switch' | 'automatic', fresh: DeviceInfo): boolean =>
    kind === 'scan'
      ? fresh.mobile_network_scan_status === 'COMPLETED' || fresh.mobile_network_scan_status === 'FAILED'
      : fresh.mobile_network_switch_state === 'CONNECTED' ||
        fresh.mobile_network_switch_state === 'FAILED' ||
        fresh.mobile_network_switch_state === 'IDLE';

  const mobileNetworkUpdatedAt = (kind: 'scan' | 'switch' | 'automatic', fresh: DeviceInfo): string | null =>
    kind === 'scan' ? fresh.mobile_network_scan_updated_at : fresh.mobile_network_switch_updated_at;

  const pollMobileNetwork = (kind: 'scan' | 'switch' | 'automatic', requestedAt: number) => {
    const deadline = Date.now() + 100_000;
    const tick = async () => {
      if (!radioId) return;
      let fresh: DeviceInfo;
      try {
        fresh = await getDeviceInfo(radioId);
      } catch {
        setTimeout(tick, 5000);
        return;
      }
      setInfo(fresh);
      const updatedAt = mobileNetworkUpdatedAt(kind, fresh);
      const gotFreshResult = updatedAt !== null && new Date(updatedAt).getTime() >= requestedAt;
      if (gotFreshResult && isMobileNetworkTerminal(kind, fresh)) {
        setMobileNetworkPending(null);
        setMobileNetworkMessage(
          kind === 'scan'
            ? fresh.mobile_network_scan_status === 'COMPLETED'
              ? 'Scan complete.'
              : 'Scan failed.'
            : fresh.mobile_network_switch_message || switchStateLabel(fresh.mobile_network_switch_state)
        );
        return;
      }
      if (Date.now() > deadline) {
        setMobileNetworkPending(null);
        setMobileNetworkMessage('Timed out waiting for the radio to respond.');
        return;
      }
      setTimeout(tick, 5000);
    };
    setTimeout(tick, 3000);
  };

  const handleMobileNetworkScan = async () => {
    if (!radioId) return;
    setMobileNetworkPending('scan');
    setMobileNetworkMessage('Scanning mobile networks... this can take up to about one minute.');
    const requestedAt = Date.now();
    try {
      await requestMobileNetworkScan(radioId);
      pollMobileNetwork('scan', requestedAt);
    } catch (e) {
      setMobileNetworkMessage(e instanceof Error ? e.message : 'Failed to request a network scan');
      setMobileNetworkPending(null);
    }
  };

  const handleMobileNetworkSwitchConfirmed = async (target: MobileNetworkOperator) => {
    if (!radioId || !target.mcc_mnc) return;
    setMobileNetworkConfirmTarget(null);
    setMobileNetworkPending('switch');
    setMobileNetworkMessage(`Switching to ${target.name || target.mcc_mnc}...`);
    const requestedAt = Date.now();
    try {
      await requestMobileNetworkSwitch(radioId, target.mcc_mnc, target.name);
      pollMobileNetwork('switch', requestedAt);
    } catch (e) {
      setMobileNetworkMessage(e instanceof Error ? e.message : 'Failed to request an operator switch');
      setMobileNetworkPending(null);
    }
  };

  const handleMobileNetworkAutomatic = async () => {
    if (!radioId) return;
    setMobileNetworkPending('automatic');
    setMobileNetworkMessage('Returning to Automatic network selection...');
    const requestedAt = Date.now();
    try {
      await requestMobileNetworkAutomatic(radioId);
      pollMobileNetwork('automatic', requestedAt);
    } catch (e) {
      setMobileNetworkMessage(e instanceof Error ? e.message : 'Failed to request automatic selection');
      setMobileNetworkPending(null);
    }
  };

  if (loading) return <div className="text-gray-400">Loading…</div>;

  if (error || !info) {
    return (
      <div className="space-y-4">
        <div className="bg-red-900/40 border border-red-700 rounded-lg p-3 text-red-200">
          {error || 'Device not found'}
        </div>
        <button
          onClick={() => navigate('/Settings/assign')}
          className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium py-1.5 px-3 rounded"
        >
          ← Back to Assign Radios
        </button>
      </div>
    );
  }

  const densityScale = info.display_density_dpi ? (info.display_density_dpi / 160).toFixed(1) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold">📱 Device Details</h2>
          <p className="text-gray-400 mt-1">
            {info.name || info.radio_id} · Radio ID: {info.radio_id || '—'} {info.org_name ? `· ${info.org_name}` : ''}
          </p>
        </div>
        <button
          onClick={() => navigate('/Settings/assign')}
          className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium py-1.5 px-3 rounded"
        >
          ← Back
        </button>
      </div>

      <Section title="Device">
        <Field label="Model" value={info.model} />
        <Field label="Product" value={info.product} />
        <Field label="Android version" value={info.android_version} />
        <Field label="Build display" value={info.build_display} />
        <Field label="Android ID" value={info.android_id} />
        <Field label="Serial number" value={info.hardware_serial} />
        <Field label="IMEI (slot 1)" value={info.imei1} />
        <Field label="IMEI (slot 2)" value={info.imei2} />
        <Field label="Uptime" value={formatUptime(info.uptime_seconds)} />
        <Field label="Device time" value={info.device_time ? new Date(info.device_time).toLocaleString() : null} />
        <div className="col-span-2 md:col-span-3 flex items-center gap-3 pt-1">
          <button
            onClick={handleReboot}
            disabled={info.status !== 'online' || powerActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-amber-700 hover:bg-amber-600 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {powerActionPending === 'reboot' ? 'Requesting…' : '🔄 Restart device'}
          </button>
          <button
            onClick={handleShutdown}
            disabled={info.status !== 'online' || powerActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-red-800 hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {powerActionPending === 'shutdown' ? 'Requesting…' : '⏻ Shut down device'}
          </button>
          {powerActionMessage && <p className="text-sm text-gray-400">{powerActionMessage}</p>}
        </div>
      </Section>

      <Section title="SIM">
        {isPlatform && info.iccid && !info.iccid_masked ? (
          <div className="col-span-2">
            <p className="text-xs text-gray-400 uppercase font-semibold">ICCID (reported by radio)</p>
            <div className="text-white"><IccidWithPortal iccid={info.iccid} /></div>
          </div>
        ) : (
          <Field label={info.iccid_masked ? 'ICCID (masked)' : 'ICCID'} value={info.iccid} />
        )}
        <Field label="SIM operator" value={info.sim_operator_name} />
        {isPlatform && (
          <div className="col-span-2 md:col-span-3">
            <p className="text-xs text-gray-400 uppercase font-semibold mb-1">1NCE SIM</p>
            <OnenceDeviceSim id={info.id} radioName={info.name || info.radio_id || undefined} />
          </div>
        )}
      </Section>

      <WifiNetworkSection info={info} now={now} onInfo={setInfo} />

      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 space-y-3">
        <h3 className="text-lg font-bold">Mobile Network</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Field label="Current operator" value={info.network_operator} />
          <Field
            label="MCC/MNC"
            value={
              info.network_operator_numeric && info.network_operator_numeric.length >= 4
                ? `${info.network_operator_numeric.slice(0, 3)}/${info.network_operator_numeric.slice(3)}`
                : info.network_operator_numeric
            }
          />
          <Field label="Technology" value={info.network_type} />
          <Field label="Roaming" value={info.network_roaming === null ? null : info.network_roaming ? 'Yes' : 'No'} />
          <Field label="Selection mode" value={info.network_selection_mode} />
          <CellularSignalField info={info} now={now} />
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-1">
          <button
            onClick={handleMobileNetworkScan}
            disabled={info.status !== 'online' || mobileNetworkPending !== null}
            title={info.status !== 'online' ? 'Radio is offline. Mobile network control is unavailable.' : undefined}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {mobileNetworkPending === 'scan' ? 'Scanning…' : '📡 Scan networks'}
          </button>
          <button
            onClick={handleMobileNetworkAutomatic}
            disabled={info.status !== 'online' || mobileNetworkPending !== null}
            title={info.status !== 'online' ? 'Radio is offline. Mobile network control is unavailable.' : undefined}
            className="bg-gray-700 hover:bg-gray-600 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {mobileNetworkPending === 'automatic' ? 'Requesting…' : 'Return to Automatic'}
          </button>
          {info.status !== 'online' && (
            <p className="text-sm text-amber-400">Radio is offline. Mobile network control is unavailable.</p>
          )}
        </div>

        {mobileNetworkPending === 'scan' && (
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
            <p className="text-sm text-gray-400">{mobileNetworkMessage}</p>
          </div>
        )}
        {mobileNetworkPending !== 'scan' && mobileNetworkMessage && (
          <p className="text-sm text-gray-400">{mobileNetworkMessage}</p>
        )}

        {(() => {
          if (!info.mobile_network_scan_result) return null;
          let operators: MobileNetworkOperator[] = [];
          try {
            operators = JSON.parse(info.mobile_network_scan_result);
          } catch {
            return null;
          }
          if (operators.length === 0) return null;
          // The scan snapshot's own "CURRENT" marker is only as fresh as the last scan - a
          // switch/automatic-reselection afterwards can make it wrong. Live heartbeat telemetry
          // (network_operator_numeric) is always the newer source of truth when present, so it
          // overrides the scan's own marker instead of the two silently disagreeing in the UI.
          const liveNumeric = info.network_operator_numeric;
          return (
            <div className="pt-2">
              <p className="text-xs text-gray-400 uppercase font-semibold mb-1">Available Networks</p>
              <div className="space-y-1">
                {operators.map((op, idx) => {
                  const isCurrent = liveNumeric ? op.mcc_mnc === liveNumeric : op.availability === 'CURRENT';
                  // Never fall back to the raw scan-time availability's own "Current" text once
                  // liveNumeric contradicts it - only downgrade a stale CURRENT to Available, so a
                  // superseded scan entry can't keep claiming to be Current alongside the real one.
                  const statusLabel = isCurrent
                    ? 'Current'
                    : op.availability === 'CURRENT'
                      ? 'Available'
                      : op.availability.charAt(0) + op.availability.slice(1).toLowerCase();
                  return (
                    <div key={`${op.mcc_mnc}-${idx}`} className="flex items-center justify-between border-t border-gray-700 pt-1">
                      <div>
                        <p className="text-white text-sm">
                          {op.name || 'Unknown'} ({op.mcc_mnc || '?'})
                        </p>
                        <p className="text-gray-400 text-xs">
                          {op.technology} · {statusLabel}
                        </p>
                      </div>
                      {!isCurrent && op.availability !== 'FORBIDDEN' && (
                        <button
                          onClick={() => setMobileNetworkConfirmTarget(op)}
                          disabled={info.status !== 'online' || mobileNetworkPending !== null}
                          className="text-blue-400 hover:text-blue-300 font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Select
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {mobileNetworkConfirmTarget && (
          <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md p-6 space-y-3">
              <h3 className="text-xl font-bold text-white">
                Switch {info.radio_id || 'this radio'} to {mobileNetworkConfirmTarget.name || mobileNetworkConfirmTarget.mcc_mnc}?
              </h3>
              <p className="text-sm text-gray-300">MCC/MNC: {mobileNetworkConfirmTarget.mcc_mnc || 'Unknown'}</p>
              <p className="text-sm text-gray-400">
                The radio may temporarily lose mobile connectivity. If registration or backend
                connectivity fails, the radio will automatically return to Automatic mode.
              </p>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  onClick={() => setMobileNetworkConfirmTarget(null)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleMobileNetworkSwitchConfirmed(mobileNetworkConfirmTarget)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded"
                >
                  Switch
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Section title="Battery">
        <Field label="Battery level" value={info.battery_level !== null ? `${info.battery_level}%` : null} />
        <Field label="Battery health" value={info.battery_health} />
        <Field label="Charging state" value={info.charging_state} />
        <Field
          label="Battery temperature"
          value={info.battery_temperature_c !== null ? `${info.battery_temperature_c} °C` : null}
        />
      </Section>

      <Section title="Display">
        <Field
          label="Resolution"
          value={
            info.display_width && info.display_height
              ? `${info.display_width} × ${info.display_height}`
              : null
          }
        />
        <Field
          label="Density"
          value={info.display_density_dpi ? `${info.display_density_dpi} dpi (${densityScale}x)` : null}
        />
        <div>
          <p className="text-xs text-gray-400 uppercase font-semibold">Screen timeout</p>
          <select
            value={displayTimeoutOptimisticMs ?? info.display_timeout_ms ?? ''}
            onChange={(e) => handleDisplayTimeoutChange(Number(e.target.value))}
            disabled={info.status !== 'online' || displayTimeoutPending}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-gray-700 text-white text-sm rounded px-2 py-1 mt-1 disabled:opacity-50"
          >
            <option value="" disabled>
              {info.display_timeout_ms ? `${info.display_timeout_ms} ms (unlisted)` : 'Unknown'}
            </option>
            {DISPLAY_TIMEOUT_OPTIONS.map((opt) => (
              <option key={opt.ms} value={opt.ms}>
                {opt.label}
              </option>
            ))}
          </select>
          {displayTimeoutPending && <p className="text-xs text-gray-400 mt-1">Applying…</p>}
        </div>
      </Section>

      {info.dmr_supported && <DmrDiagnostics status={info.dmr ?? null} />}

      <Section title="Location">
        <Field label="Latitude" value={info.latitude !== null ? info.latitude.toFixed(6) : null} />
        <Field label="Longitude" value={info.longitude !== null ? info.longitude.toFixed(6) : null} />
        <Field label="Accuracy" value={info.accuracy_m !== null ? `${info.accuracy_m.toFixed(1)} m` : null} />
        <Field label="Altitude" value={info.altitude_m !== null ? `${info.altitude_m.toFixed(1)} m` : null} />
        <Field
          label="Speed"
          value={info.speed_mps !== null ? `${(info.speed_mps * 3.6).toFixed(1)} km/h` : null}
        />
        <Field label="Heading" value={info.bearing_deg !== null ? `${Math.round(info.bearing_deg)}°` : null} />
        <Field label="Provider" value={info.location_provider} />
        <Field
          label="GPS timestamp"
          value={info.location_timestamp ? new Date(info.location_timestamp).toLocaleString() : null}
        />
        <Field
          label="GPS now"
          value={info.gps_status === 'valid' ? 'Fix available' : info.gps_status ? 'No current fix' : null}
        />
        <Field
          label="Last received"
          value={info.location_received_at ? new Date(info.location_received_at).toLocaleString() : null}
        />
        {info.latitude !== null && info.longitude !== null && (
          <div className="col-span-2 md:col-span-3">
            <a
              href={`https://www.openstreetmap.org/?mlat=${info.latitude}&mlon=${info.longitude}#map=17/${info.latitude}/${info.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 hover:underline text-sm"
            >
              Open location in map ↗
            </a>
          </div>
        )}
      </Section>

      <Section title="Reporting">
        <Field
          label="Last device information update"
          value={info.device_info_updated_at ? new Date(info.device_info_updated_at).toLocaleString() : null}
        />
        {/* Registration-time values of whichever transport was active (Wi-Fi or mobile): never
            shown as a Wi-Fi or a cellular reading. */}
        <Field
          label="Active-network signal at registration"
          value={info.signal_strength_dbm !== null ? `${info.signal_strength_dbm} dBm (Wi-Fi or mobile)` : null}
        />
        <div className="col-span-2 md:col-span-3">
          <p className="text-xs text-gray-400 uppercase font-semibold">IP addresses at registration (all interfaces)</p>
          {info.ip_addresses && info.ip_addresses.length > 0 ? (
            <ul className="text-white list-disc list-inside">
              {info.ip_addresses.map((ip) => (
                <li key={ip} className="font-mono text-sm">{ip}</li>
              ))}
            </ul>
          ) : (
            <p className="text-white">{NOT_AVAILABLE}</p>
          )}
        </div>
        <div className="col-span-2 md:col-span-3 flex items-center gap-3">
          <button
            onClick={handleRefresh}
            disabled={info.status !== 'online' || refreshing}
            title={info.status !== 'online' ? 'Device must be online to request a refresh' : undefined}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {refreshing ? 'Requesting…' : '🔄 Update / Retrieve Data Again'}
          </button>
          {refreshMessage && <p className="text-sm text-gray-400">{refreshMessage}</p>}
        </div>
      </Section>

      <Section title="Logs">
        <div className="col-span-2 md:col-span-3 flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleCollectLog('logcat')}
            disabled={info.status !== 'online' || logActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {logActionPending === 'logcat' ? 'Requesting…' : '📋 Logcat'}
          </button>
          <button
            onClick={handleClearLogcat}
            disabled={info.status !== 'online' || logActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {logActionPending === 'clear' ? 'Requesting…' : '🗑️ Ryd logcat'}
          </button>
          <button
            onClick={() => handleCollectLog('dmesg')}
            disabled={info.status !== 'online' || logActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {logActionPending === 'dmesg' ? 'Requesting…' : '⚙️ dmesg'}
          </button>
          <button
            onClick={() => handleCollectLog('crash')}
            disabled={info.status !== 'online' || logActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {logActionPending === 'crash' ? 'Requesting…' : '💥 Crashes/ANRs'}
          </button>
          <button
            onClick={() => handleCollectLog('wakelocks')}
            disabled={info.status !== 'online' || logActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {logActionPending === 'wakelocks' ? 'Requesting…' : '🔋 Wake Locks'}
          </button>
          <button
            onClick={() => handleCollectLog('all_zip')}
            disabled={info.status !== 'online' || logActionPending !== null}
            title={info.status !== 'online' ? 'Device must be online' : undefined}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium py-1.5 px-3 rounded"
          >
            {logActionPending === 'all_zip' ? 'Requesting…' : '📦 Hent alle logs (ZIP)'}
          </button>
        </div>
        {logMessage && <p className="col-span-2 md:col-span-3 text-sm text-gray-400">{logMessage}</p>}

        <div className="col-span-2 md:col-span-3">
          {logs.length === 0 ? (
            <p className="text-gray-400 text-sm">No logs collected yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-400 uppercase text-xs">
                  <th className="pb-1">Type</th>
                  <th className="pb-1">Collected</th>
                  <th className="pb-1">Size</th>
                  <th className="pb-1"></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} className="border-t border-gray-700">
                    <td className="py-1">
                      {LOG_TYPES.find((t) => t.type === log.log_type)?.label || log.log_type}
                    </td>
                    <td className="py-1 text-gray-300">{new Date(log.created_at).toLocaleString()}</td>
                    <td className="py-1 text-gray-300">{formatBytes(log.file_size_bytes)}</td>
                    <td className="py-1 text-right">
                      <a
                        href={downloadDeviceLogUrl(log.id)}
                        className="text-blue-400 hover:text-blue-300 hover:underline"
                      >
                        Download
                      </a>
                      <button
                        onClick={() => handleDeleteLog(log.id)}
                        disabled={logActionPending !== null}
                        className="ml-3 text-red-400 hover:text-red-300 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {logActionPending === `delete-${log.id}` ? 'Deleting…' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Section>
    </div>
  );
}
